import type { ReactNode } from 'react';
import { SEV_DOT } from '../components/ui/Badges';
import { PageHeader } from '../components/ui/Feedback';
import { CATEGORY_LABEL, SEVERITY_LABEL } from '../lib/format';
import { CATEGORIES, SEVERITIES } from '../types/api';

const SEVERITY_HELP: Record<(typeof SEVERITIES)[number], string> = {
  critical: 'Likely exploitable security flaw or data loss.',
  high: 'Likely bug in normal use.',
  medium: 'Bug in edge cases or under specific conditions.',
  low: 'Minor risk worth fixing when convenient.',
  info: 'Worth knowing; not a defect.',
};

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="scroll-mt-20">
      <h2 id={`${id}-h`} className="text-base font-semibold">
        {title}
      </h2>
      <div className="mt-2 space-y-3 text-sm leading-relaxed text-muted">{children}</div>
    </section>
  );
}

const TOC = [
  ['how', 'How a review works'],
  ['reading', 'Reading findings'],
  ['severity', 'Severity & categories'],
  ['publishing', 'Publishing to GitHub'],
  ['demo', 'Demo mode'],
  ['privacy', 'Privacy & security'],
  ['limits', 'Limitations'],
] as const;

export function DocsPage() {
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_14rem]">
      <div className="max-w-3xl">
        <PageHeader title="Documentation" color="lilac" description="How PR Pilot reviews code, and how to read its results." />
        <div className="card space-y-8 p-6">
          <Section id="how" title="How a review works">
            <ol className="list-decimal space-y-1 pl-5">
              <li>You submit a GitHub Pull Request URL. The server validates it (github.com only) and fetches PR metadata.</li>
              <li>If an identical review (same PR and commit) is already running, you are taken to it instead of starting a duplicate.</li>
              <li>The changed files and their diffs are fetched. Lock files, generated/minified files, binaries and oversized diffs are skipped and listed.</li>
              <li>The remaining diffs are sent to AI Pilot with strict review criteria and a structured output schema.</li>
              <li>The response is validated. File paths and line numbers are checked against the real diff; duplicates and findings about unknown files are dropped.</li>
              <li>Results are saved, and the review moves through <em>queued → processing → completed</em> (or <em>failed</em>, with a retry option).</li>
            </ol>
          </Section>

          <Section id="reading" title="Reading findings">
            <p>
              Every finding is a <strong className="text-ink">potential issue</strong>, not a confirmed defect. Each includes an explanation, evidence from the
              diff, potential impact, a suggested fix and the model&apos;s confidence.
            </p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong className="text-ink">Line verified in diff</strong> — the cited line exists in this PR&apos;s diff.
              </li>
              <li>
                <strong className="text-ink">File-level</strong> — the model&apos;s line number could not be matched, so the finding is attached to the file.
              </li>
              <li>
                <strong className="text-ink">Needs context</strong> — confirming it requires code outside the diff.
              </li>
            </ul>
            <p>Use Accept, Dismiss (with an optional reason) and Mark resolved to track what you did with each finding.</p>
          </Section>

          <Section id="severity" title="Severity & categories">
            <ul className="space-y-1.5">
              {SEVERITIES.map((s) => (
                <li key={s} className="flex items-start gap-2">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEV_DOT[s]}`} aria-hidden />
                  <span>
                    <strong className="text-ink">{SEVERITY_LABEL[s]}</strong> — {SEVERITY_HELP[s]}
                  </span>
                </li>
              ))}
            </ul>
            <p>Categories: {CATEGORIES.map((c) => CATEGORY_LABEL[c]).join(', ')}.</p>
          </Section>

          <Section id="publishing" title="Publishing to GitHub">
            <p>
              Select findings, preview the exact comments, and confirm. They are posted as one review with inline comments on verified lines; other findings go into
              the review body. If new commits made line positions outdated, comments are moved into the review body. PR Pilot never pushes code, applies
              patches, or merges Pull Requests.
            </p>
          </Section>

          <Section id="demo" title="Demo mode">
            <p>
              Without AI credentials on the server (or with <span className="font-mono">DEMO_MODE=true</span>) you can review the sample PRs under{' '}
              <span className="font-mono">pr-pilot-demo/*</span>. Their findings are deterministic sample data and are always labelled{' '}
              <em>Demo data</em>. They are never presented as AI Pilot analysis.
            </p>
          </Section>

          <Section id="privacy" title="Privacy & security">
            <ul className="list-disc space-y-1 pl-5">
              <li>Only the changed hunks of reviewed files are sent to the model — not the whole repository.</li>
              <li>Repository content is treated as untrusted input; instructions embedded in code or PR text are ignored by the reviewer.</li>
              <li>API keys stay in server environment variables and are never sent to the browser or logged.</li>
              <li>Diff hunks are stored with reviews for the Changed Files view (disable with STORE_PATCHES=false) and expire with the retention policy.</li>
            </ul>
          </Section>

          <Section id="limits" title="Limitations">
            <ul className="list-disc space-y-1 pl-5">
              <li>The reviewer only sees the diff, so cross-file issues may be missed or flagged with lower confidence.</li>
              <li>AI findings can be wrong. No accuracy is claimed unless measured with the evaluation dataset (npm run eval).</li>
              <li>Very large PRs are partially analyzed; skipped files are always listed.</li>
            </ul>
          </Section>
        </div>
      </div>
      <nav aria-label="On this page" className="hidden lg:block">
        <div className="sticky top-20">
          <p className="label">On this page</p>
          <ul className="mt-2 space-y-1.5 text-sm">
            {TOC.map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="text-muted hover:text-accent">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </div>
  );
}
